import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Pencil, Plus, Trash2 } from "lucide-react";
import { NuevaReservaDialog, type ReservaFormValues } from "@/components/gestion/reservas/NuevaReservaDialog";
import { useServerFn } from "@tanstack/react-start";
import { cambiarEstadoReserva } from "@/lib/reservas.functions";

type Reserva = {
  id: string;
  habitacion_id: string;
  nombre_huesped: string;
  email_huesped: string;
  telefono_huesped: string;
  fecha_entrada: string;
  fecha_salida: string;
  num_huespedes: number;
  precio_total: number;
  senal_pagada: number;
  estado: string;
  notas: string;
  canal?: string;
  metodo_pago?: string | null;
  cliente_id?: string | null;
};

const ESTADOS = ["pendiente", "confirmada", "cancelada", "completada"] as const;

export const Route = createFileRoute("/_authenticated/gestion/reservas/")({
  component: ReservasPage,
});

function ReservasPage() {
  const cambiarEstado = useServerFn(cambiarEstadoReserva);
  const [items, setItems] = useState<Reserva[]>([]);
  const [habs, setHabs] = useState<{ id: string; nombre: string; capacidad?: number }[]>([]);
  const [filter, setFilter] = useState<string>("todas");
  const [editing, setEditing] = useState<Partial<ReservaFormValues> | null>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const { data, error } = await supabase.from("reservas").select("*").order("fecha_entrada", { ascending: false });
    if (error) return toast.error(error.message);
    setItems((data ?? []) as Reserva[]);
  }
  async function loadHabs() {
    const { data } = await supabase.from("habitaciones").select("id, nombre, capacidad").order("orden");
    setHabs(data ?? []);
  }
  useEffect(() => { load(); loadHabs(); }, []);

  const filtered = useMemo(
    () => (filter === "todas" ? items : items.filter((r) => r.estado === filter)),
    [items, filter],
  );
  const habName = (id: string) => habs.find((h) => h.id === id)?.nombre ?? "—";

  function openNew() {
    setEditing({ habitacion_id: habs[0]?.id, canal: "telefono" });
    setOpen(true);
  }
  function openEdit(r: Reserva) {
    setEditing({
      id: r.id,
      habitacion_id: r.habitacion_id,
      cliente_id: r.cliente_id ?? null,
      nombre_huesped: r.nombre_huesped,
      email_huesped: r.email_huesped,
      telefono_huesped: r.telefono_huesped,
      fecha_entrada: r.fecha_entrada,
      fecha_salida: r.fecha_salida,
      num_huespedes: r.num_huespedes,
      precio_total: Number(r.precio_total),
      senal_pagada: Number(r.senal_pagada),
      estado: r.estado,
      notas: r.notas,
      canal: r.canal ?? "directo",
      metodo_pago: r.metodo_pago ?? "pendiente",
    });
    setOpen(true);
  }

  async function remove(id: string) {
    if (!confirm("¿Eliminar la reserva?")) return;
    const { error } = await supabase.from("reservas").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  }

  async function changeEstado(r: Reserva, estado: string) {
    try {
      await cambiarEstado({ data: { id: r.id, estado: estado as "pendiente" | "confirmada" | "cancelada" | "completada" } });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Estado:</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNew}><Plus className="mr-2 h-4 w-4" />Nueva reserva</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase text-muted-foreground">
              <tr><th className="p-3">Huésped</th><th>Habitación</th><th>Entrada</th><th>Salida</th><th>Pax</th><th>Total</th><th>Estado</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="p-3"><div className="font-medium">{r.nombre_huesped}</div><div className="text-xs text-muted-foreground">{r.email_huesped}</div></td>
                  <td>{habName(r.habitacion_id)}</td>
                  <td>{r.fecha_entrada}</td>
                  <td>{r.fecha_salida}</td>
                  <td>{r.num_huespedes}</td>
                  <td>{Number(r.precio_total).toFixed(2)} €</td>
                  <td>
                    <Select value={r.estado} onValueChange={(v) => changeEstado(r, v)}>
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    <Button asChild size="icon" variant="ghost" title="Ficha (viajeros y factura)">
                      <Link to="/gestion/reservas/$id" params={{ id: r.id }}><FileText className="h-4 w-4" /></Link>
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Sin reservas.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <NuevaReservaDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        habitaciones={habs}
        initial={editing}
        onSaved={() => { load(); }}
      />
    </div>
  );
}